import useSWR from 'swr';

import { dateToDateString } from '~/components/utils/dateUtils';
import { useGlobalFilterContext } from '~/contexts/globalFilterContext';
import { portfoliosKpisRetrieve } from '~/data/generated/endpoints/portfolios/portfolios';
import type { KPIContract } from '~/data/generated/model';

/**
 * How one contract is performing, over a period.
 *
 * `byContract` aggregates over whatever property scope the request names, so passing
 * the contract's own properties gives "this contract, everywhere it is in force"; the
 * row for any other contract covering those buildings is dropped here.
 *
 * `dateRange` is the period the Performance card owns — a contract's page carries no
 * global filter band. It is only ever part of the SWR key, so a local range neither
 * writes to the global filter nor collides with a caller using it.
 */
export function useContractKpis(
  contractId: string | undefined,
  propertyIds: readonly string[],
  { dateRange }: { dateRange?: { start: Date; end: Date } } = {},
) {
  const {
    selectedFilters: {
      date: { start: globalStart, end: globalEnd },
    },
  } = useGlobalFilterContext();
  const start = dateRange?.start ?? globalStart;
  const end = dateRange?.end ?? globalEnd;

  // Sorted so two renders that name the same properties in a different order
  // share one cache entry rather than refetching.
  const scope = [...propertyIds].sort();

  const { data, error, isLoading } = useSWR(
    // A contract covering no property has nothing to aggregate over: the request
    // would be scoped to every property the user can see and report another
    // contract's work under this one's name.
    contractId && scope.length > 0
      ? ([
          'contract-kpis',
          contractId,
          scope,
          dateToDateString(start),
          dateToDateString(end),
        ] as const)
      : null,
    async ([, id, properties, startDate, endDate]) => {
      const { data: kpis } = await portfoliosKpisRetrieve({
        property: [...properties],
        start_date: startDate,
        end_date: endDate,
      });
      return (kpis.byContract as KPIContract[]).find((row) => row.contract === id) ?? null;
    },
  );

  return { kpis: data ?? null, error, isLoading };
}
